// Prepaid credit rules shared by the portal, the backend and the pricing page. Amounts are in minor
// units of the billing currency (pence). Kept free of zod so the landing page can import it.

/** The smallest and largest credit purchase, in minor units. */
export const CREDIT_PURCHASE_MIN_MINOR = 1_000;
export const CREDIT_PURCHASE_MAX_MINOR = 500_000;

/** The amounts offered as one-click options when buying credit. */
export const CREDIT_PURCHASE_PRESETS_MINOR = [5_000, 10_000, 50_000] as const;

/**
 * Bonus credit added to a purchase of at least `fromMinor`, as a percentage of the amount paid.
 * Highest threshold first.
 */
export const CREDIT_BONUS_TIERS: readonly { fromMinor: number; percent: number }[] = [
  { fromMinor: 50_000, percent: 8 },
  { fromMinor: 10_000, percent: 5 },
  { fromMinor: 5_000, percent: 2 },
];

/** Bonus credit expires this many months after it is granted; paid credit never expires. */
export const BONUS_CREDIT_EXPIRY_MONTHS = 12;

/** Spend alerts are emailed once per billing period when spend crosses each of these percentages. */
export const SPEND_ALERT_PERCENTS = [50, 80, 100] as const;

/** Auto top-up and spend limit amounts, in minor units. */
export const AUTO_TOP_UP_THRESHOLD_MIN_MINOR = 500;
export const SPEND_LIMIT_MIN_MINOR = 1_000;
export const SPEND_LIMIT_MAX_MINOR = 10_000_000;

/** Returned (409) when credit is bought or auto top-up enabled before billing is set up. */
export const BILLING_REQUIRED_ERROR_CODE = "payment/billing-required";
/** Returned (409) when auto top-up is enabled without a saved card. */
export const PAYMENT_METHOD_REQUIRED_ERROR_CODE = "payment/payment-method-required";

export const isValidCreditPurchaseAmount = (amountMinor: number): boolean =>
  Number.isInteger(amountMinor) &&
  amountMinor >= CREDIT_PURCHASE_MIN_MINOR &&
  amountMinor <= CREDIT_PURCHASE_MAX_MINOR;

export const getCreditBonusPercent = (amountMinor: number): number =>
  CREDIT_BONUS_TIERS.find((tier) => amountMinor >= tier.fromMinor)?.percent ?? 0;

/** The bonus credit for a purchase, rounded down to whole minor units. */
export const getCreditBonusMinor = (amountMinor: number): number =>
  Math.floor((amountMinor * getCreditBonusPercent(amountMinor)) / 100);

/** The newest alert percentage that `spentMinor` has reached against `limitMinor`, or 0. */
export const getReachedSpendAlertPercent = (spentMinor: number, limitMinor: number): number => {
  if (limitMinor <= 0) return 0;
  const percent = (spentMinor / limitMinor) * 100;
  return [...SPEND_ALERT_PERCENTS].reverse().find((threshold) => percent >= threshold) ?? 0;
};
